import { FC, useEffect, useId, useRef, useState } from 'react';

import { OutsideClickComponent } from '@telialt/common-outside-click/src';

import { InputField } from './components/InputField';
import { Suggestions } from './components/Suggestions';
import { InputWithSuggestionsWrapper } from './InputWithSuggestions.styles';
import { InputProps } from './model';
import { InputContextProvider } from './providers/InputContextProvider';

export const InputWithSuggestions: FC<InputProps> = (props) => {
    const id = useId();
    const suggestionsId = `suggestions-${id}`;
    const suggestionsWrapperId = `suggestions-wrapper-${id}`;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);

    const handleOutsideClick = () => {
        setIsSuggestionsVisible(false);
    };

    const handleInputFocus = () => {
        setIsSuggestionsVisible(true);
    };

    const handleShowSuggestions = () => {
        setIsSuggestionsVisible(true);
    };

    const handleHideSuggestions = () => {
        setIsSuggestionsVisible(false);
    };

    useEffect(() => {
        const containerNode = containerRef.current;

        function handleFocusOut(e: FocusEvent) {
            const suggestionsWrapper = containerNode.querySelector(`#${suggestionsWrapperId}`);
            if (!containerNode.contains(e.relatedTarget as Node) && isSuggestionsVisible && suggestionsWrapper) {
                inputRef.current?.focus();
            }
        }

        containerNode?.addEventListener('focusout', handleFocusOut);

        return () => {
            containerNode?.removeEventListener('focusout', handleFocusOut);
        };
    }, [isSuggestionsVisible]);

    return (
        <InputContextProvider
            isSuggestionsVisible={isSuggestionsVisible}
            onShowSuggestions={handleShowSuggestions}
            onHideSuggestions={handleHideSuggestions}
            {...props}
        >
            <OutsideClickComponent onClickedOutside={handleOutsideClick}>
                <InputWithSuggestionsWrapper ref={containerRef}>
                    <InputField
                        aria-controls={suggestionsId}
                        aria-autocomplete="list"
                        role="combobox"
                        aria-expanded={isSuggestionsVisible}
                        ref={inputRef}
                        onFocus={handleInputFocus}
                    />
                    {isSuggestionsVisible && <Suggestions id={suggestionsId} wrapperId={suggestionsWrapperId} />}
                </InputWithSuggestionsWrapper>
            </OutsideClickComponent>
        </InputContextProvider>
    );
};
